/**
 * MedGlobe floating pill header — compact teardrop (right), section rail.
 * Corporate homepage uses Athora-style document scroll (not Horizon page-wrapper).
 */
(() => {
  const root = document.querySelector("[data-medglobe-header]");
  if (!root || root.dataset.mgHeaderReady) return;
  root.dataset.mgHeaderReady = "true";

  const sticky =
    root.querySelector("[data-mg-sticky]") ||
    root.querySelector("hdt-sticky-header") ||
    root.querySelector(".hdt-sticky-header");
  const wrapper = root.querySelector(".hdt-header-wrapper");
  const drawer = root.querySelector("[data-mg-menu-drawer]");
  const drawerOpenBtn = root.querySelector("[data-mg-drawer-open]");
  const sectionEl = root.closest(".shopify-section") || root;
  const reduce = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

  const setDrawerExpanded = (open) => {
    if (drawerOpenBtn) drawerOpenBtn.setAttribute("aria-expanded", open ? "true" : "false");
  };

  const closeDrawer = () => {
    if (typeof drawer?.close === "function") drawer.close();
    else drawer?.removeAttribute("open");
    setDrawerExpanded(false);
  };

  const openDrawer = () => {
    if (typeof drawer?.showModal === "function") drawer.showModal();
    else drawer?.setAttribute("open", "");
    setDrawerExpanded(true);
  };

  document.body.classList.add("mg-sticky-right");
  document.body.classList.remove("mg-sticky-left");

  root.classList.add("shopify-section-header-sticky");
  sectionEl.classList.add("shopify-section-header-sticky");

  if (sticky) {
    sticky.setAttribute("sticky-inted", "");
    sticky.classList.add("mg-sticky-ready");
  }

  drawerOpenBtn?.addEventListener("click", openDrawer);
  root.querySelector("[data-mg-drawer-close]")?.addEventListener("click", closeDrawer);
  drawer?.addEventListener("click", (event) => {
    if (event.target === drawer) closeDrawer();
  });
  drawer?.addEventListener("close", () => setDrawerExpanded(false));

  const freezeHeight = () => {
    if (root.classList.contains("mg-header-compact")) return;
    if (!sticky || !wrapper) return;
    const inner = Math.max(wrapper.getBoundingClientRect().height, 40);
    sticky.style.setProperty("--header-inner-height", `${inner}px`);
    sticky.style.height = "";
    const height = Math.max(sticky.getBoundingClientRect().height || 0, inner + 16);
    sticky.style.height = `${height.toFixed(2)}px`;
    document.documentElement.style.setProperty("--mg-header-height", `${height.toFixed(2)}px`);
  };
  addEventListener("resize", freezeHeight);
  addEventListener("load", freezeHeight);
  freezeHeight();
  requestAnimationFrame(freezeHeight);

  const scrollScopes = [root, document.getElementById("mg-section-rail")].filter(Boolean);
  scrollScopes.forEach((scope) => {
    scope.querySelectorAll('a[href*="#"]').forEach((link) => {
      link.addEventListener("click", (event) => {
        const href = link.getAttribute("href");
        if (!href) return;

        let url;
        try {
          url = new URL(href, location.href);
        } catch {
          return;
        }

        // Cross-page links (e.g. /#about from /policies/...) — let the browser navigate
        if (url.origin !== location.origin || url.pathname !== location.pathname) return;

        const selector = url.hash;
        if (!selector || selector === "#") return;
        const target = selector === "#top" ? document.body : document.querySelector(selector);
        if (!target) return;
        event.preventDefault();
        const behavior = reduce() ? "auto" : "smooth";
        if (selector === "#top") {
          window.scrollTo({ top: 0, behavior });
        } else {
          target.scrollIntoView({ behavior, block: "start" });
        }
        if (location.hash !== selector) history.pushState(null, "", selector);
        closeDrawer();
      });
    });
  });

  // Presence / in-page CTAs outside the header still get smooth scroll when targeting section ids
  document.querySelectorAll(".mg-presence__cta[href^='#']").forEach((link) => {
    link.addEventListener("click", (event) => {
      const selector = link.getAttribute("href");
      if (!selector || selector === "#") return;
      const target = document.querySelector(selector);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduce() ? "auto" : "smooth", block: "start" });
      if (location.hash !== selector) history.pushState(null, "", selector);
    });
  });

  let compact = false;
  const playTeardrop = () => {
    if (reduce()) return;
    root.classList.remove("mg-header-teardrop");
    void root.offsetWidth;
    root.classList.add("mg-header-teardrop");
    clearTimeout(playTeardrop.timer);
    playTeardrop.timer = setTimeout(() => root.classList.remove("mg-header-teardrop"), 760);
  };

  const syncCompact = () => {
    const next = window.scrollY > 80;
    if (next && !compact) {
      root.classList.add("mg-header-compact");
      playTeardrop();
    } else if (!next && compact) {
      root.classList.remove("mg-header-compact", "mg-header-teardrop");
    } else {
      root.classList.toggle("mg-header-compact", next);
    }
    compact = next;

    const rail = document.getElementById("mg-section-rail");
    if (!rail) return;
    rail.classList.toggle("mg-rail--visible", next);
    const links = [...rail.querySelectorAll('a[href^="#"]')];
    const line = Math.max(160, innerHeight * 0.28);
    let active;
    links.forEach((link) => {
      const target = document.querySelector(link.getAttribute("href"));
      if (target && target.getBoundingClientRect().top <= line) active = link;
    });
    links.forEach((link) =>
      link === active ? link.setAttribute("aria-current", "true") : link.removeAttribute("aria-current")
    );
  };

  addEventListener("scroll", syncCompact, { passive: true });
  addEventListener("resize", syncCompact);
  syncCompact();
})();
